import { useState } from "react";

import { useAiBrief, useAiBriefUret } from "@/api/ai";
import { ApiError } from "@/api/client";
import { usePublicSettingsQuery } from "@/api/settings";
import { AiNote } from "@/components/AiNote";
import type { Period } from "@/components/PeriodTabs";
import { Button } from "@/components/ui";
import { t } from "@/i18n";
import { fmtDateTime } from "@/lib/utils";

interface AiBriefPanelProps {
  days: Period;
  onAskFollowUp: (question: string) => void;
}

const KAYNAK_TR: Record<string, string> = { zamanlayici: "zamanlayıcı", kullanici: "kullanıcı", oto: "otomatik" };
const CHIP_LIMIT = 4;

function hataMesaji(error: unknown): string {
  if (error instanceof ApiError) return typeof error.detail === "string" ? error.detail : error.message;
  return t("common.error");
}

/**
 * AI Gidişat Notu — katlanabilir kart (eski `gd-ai <details>`): baslikta
 * uretim bilgisi + "Yeniden üret"; govde uzunsa 8 satira kirpilir ("devamını
 * göster"); takip cipleri en fazla 4 (+N daha). KULLANICI KARARI
 * (10.09.2026): not ASLA otomatik uretilmez — yalniz dugmeyle (kredi).
 */
export function AiBriefPanel({ days, onAskFollowUp }: AiBriefPanelProps) {
  const publicSettingsQuery = usePublicSettingsQuery();
  const aiDisabled = publicSettingsQuery.data?.["ai.enabled"] === false;
  const briefQuery = useAiBrief(days);
  const uretMutation = useAiBriefUret();
  const [tamMetin, setTamMetin] = useState(false);
  const [tumCipler, setTumCipler] = useState(false);

  const data = briefQuery.data;
  const brief = data?.status === "ready" ? data.brief : null;
  const isGenerating = uretMutation.isPending;
  const stale = Boolean(data && "stale" in data && data.stale);
  const followUps = brief?.follow_up ?? [];
  const gorunenCipler = tumCipler ? followUps : followUps.slice(0, CHIP_LIMIT);

  return (
    <details open className="card group" data-testid="ai-brief-panel">
      <summary className="flex cursor-pointer list-none flex-wrap items-center gap-x-3 gap-y-2 [&::-webkit-details-marker]:hidden">
        <span className="font-display text-[15px] font-bold text-ink">🧠 {t("ozet.ai.baslik")}</span>
        {brief && (
          <span className="text-xs text-muted" data-testid="ai-brief-meta">
            {briefMeta(brief, stale)}
          </span>
        )}
        <span className="ml-auto flex items-center gap-2" onClick={(e) => e.preventDefault()}>
          {!aiDisabled && (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.preventDefault();
                uretMutation.mutate({ days, force: true });
              }}
              disabled={isGenerating}
              title={t("ozet.ai.yeniden_uret_ipucu")}
            >
              {isGenerating ? t("ozet.ai.hazirlaniyor") : `↻ ${t("ozet.ai.yeniden_uret")}`}
            </Button>
          )}
          <span aria-hidden="true" className="text-xs text-muted group-open:hidden">
            {t("ozet.ai.goster")}
          </span>
          <span aria-hidden="true" className="hidden text-xs text-muted group-open:inline">
            {t("ozet.ai.gizle")}
          </span>
        </span>
      </summary>

      <div className="mt-3 space-y-3">
        {aiDisabled && (
          <p className="text-sm text-muted" data-testid="ai-disabled-notice">
            {t("ozet.ai.kapali")}
          </p>
        )}
        {!aiDisabled && briefQuery.isLoading && <p className="text-sm text-muted">{t("common.loading")}</p>}
        {!aiDisabled && briefQuery.isError && (
          <p role="alert" className="text-sm text-red">
            {t("ozet.ai.hata", { detay: hataMesaji(briefQuery.error) })}
          </p>
        )}
        {!aiDisabled && !briefQuery.isLoading && !briefQuery.isError && (
          <>
            {isGenerating && <p className="text-sm text-muted">{t("ozet.ai.hazirlaniyor")}</p>}

            {!isGenerating && brief && (
              <div>
                <div className={tamMetin ? "" : "relative max-h-[13.5rem] overflow-hidden"}>
                  <AiNote markdown={brief.markdown} />
                  {!tamMetin && (
                    <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-card to-transparent" />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setTamMetin((v) => !v)}
                  aria-expanded={tamMetin}
                  className="mt-1 text-xs font-semibold text-brand-primary hover:underline"
                >
                  {tamMetin ? t("ozet.ai.daha_az") : t("ozet.ai.devamini_goster")}
                </button>

                {followUps.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="ai-followups">
                    {gorunenCipler.map((question) => (
                      <button
                        key={question}
                        type="button"
                        onClick={() => onAskFollowUp(question)}
                        className="min-h-[34px] rounded-full border-[1.5px] border-line bg-bg px-3 text-xs font-medium text-brand-primary hover:border-blue/60 hover:bg-blue-pale"
                      >
                        {question}
                      </button>
                    ))}
                    {followUps.length > CHIP_LIMIT && (
                      <button
                        type="button"
                        onClick={() => setTumCipler((v) => !v)}
                        className="min-h-[34px] rounded-full px-3 text-xs font-semibold text-muted hover:text-ink"
                      >
                        {tumCipler ? t("ozet.ai.daha_az") : `+${followUps.length - CHIP_LIMIT} ${t("ozet.ai.daha")}`}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {!isGenerating && !brief && <p className="text-sm text-muted">{t("ozet.ai.bos")}</p>}

            {uretMutation.isError && (
              <p role="alert" className="text-sm text-red">
                {t("ozet.ai.uretim_hata", { detay: hataMesaji(uretMutation.error) })}
              </p>
            )}
          </>
        )}
      </div>
    </details>
  );
}

function briefMeta(
  brief: { generated_at?: string | null; model?: string | null; kaynak?: string | null },
  stale: boolean,
): string {
  const parts: string[] = [];
  if (brief.generated_at) parts.push(`${t("ozet.ai.uretim")}: ${fmtDateTime(brief.generated_at)}`);
  if (brief.kaynak) parts.push(KAYNAK_TR[brief.kaynak] ?? brief.kaynak);
  if (stale) parts.push(t("ozet.ai.bayat"));
  return parts.join(" · ");
}
